import apiClient, { unwrap } from './client';
import { COUNTRIES } from '../config/plans';

export async function loginApi(email, password) {
  const res = await apiClient.post('/auth/login', { email, password });
  const api = unwrap(res); // { token, userId, tenantId, email, fullName, role, plan }
  return buildSession(api, null);
}

export async function registerApi({ farmName, email, password, fullName, phoneNumber, dialCode, country, currency, acceptedTerms, privacyVersion, farmModules }) {
  const res = await apiClient.post('/auth/register', {
    farmName,
    email,
    password,
    fullName,
    phoneNumber,
    dialCode,
    country,
    currency,
    acceptedTerms,
    privacyVersion,
    farmModules: farmModules || ['POULTRY'],
  });
  const api = unwrap(res);
  return buildSession(api, country);
}

export async function forgotPasswordApi(email) {
  const res = await apiClient.post('/auth/forgot-password', { email });
  return unwrap(res);
}

export async function resetPasswordApi(token, newPassword) {
  const res = await apiClient.post('/auth/reset-password', { token, newPassword });
  return unwrap(res);
}

export async function verifyEmailApi(token) {
  const res = await apiClient.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
  return unwrap(res); // Returns { success: true, message: 'Email verified successfully' }
}

export async function resendVerificationApi(email) {
  const res = await apiClient.post('/auth/resend-verification', { email });
  return unwrap(res);
}

// ── Paid Registration Flow ─────────────────────────────────────────────────

/**
 * Register with a paid plan (creates pending registration).
 */
export async function registerPendingApi({ farmName, email, password, fullName, country, 
                                         acceptedTerms, privacyVersion, planCode, 
                                         durationMonths, phone, phoneCountryCode }) {
  const res = await apiClient.post('/auth/register/pending', {
    farmName,
    email,
    password,
    fullName,
    country,
    acceptedTerms,
    privacyVersion,
    planCode,
    durationMonths,
    phone,
    phoneCountryCode,
  });
  return unwrap(res); // { token, email, planCode, durationMonths, totalAmount, currency, expiresAt }
}

/**
 * Check if a pending registration is still valid.
 */
export async function checkRegistrationStatusApi(token) {
  const res = await apiClient.get(`/auth/register/status/${token}`);
  return unwrap(res);
}

/**
 * Prepare payment for pending registration (get Razorpay order details).
 */
export async function prepareRegistrationPaymentApi(token) {
  const res = await apiClient.get(`/billing/registration/${token}/payment`);
  return unwrap(res); // { orderId, razorpayOrderId, amount, currency, key, ... }
}

/**
 * Complete registration after successful payment.
 */
export async function completeRegistrationApi(token, razorpayOrderId, razorpayPaymentId, razorpaySignature) {
  const res = await apiClient.post('/billing/complete-registration', {
    token,
    razorpayOrderId,
    razorpayPaymentId,
    razorpaySignature,
  });
  const api = unwrap(res);
  return buildSession(api, null);
}

// After login we don't have country yet — fetch tenant to get it
export async function fetchTenantProfile(token) {
  const res = await apiClient.get('/tenants/me', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return unwrap(res); // { id, name, countryCode, currency, plan, ... }
}

export async function fetchMyProfile(token) {
  const res = await apiClient.get('/profiles/me', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return unwrap(res); // { id, name, email, phone, role, ... }
}

// ── Biometric Authentication ────────────────────────────────────────────────

/**
 * Check if user has biometric credentials registered
 */
export async function checkBiometricAvailabilityApi(email) {
  const res = await apiClient.post('/auth/biometric/check-availability', { email });
  return unwrap(res); // { hasBiometricCredentials: boolean }
}

/**
 * Start biometric registration (step 1)
 */
export async function startBiometricRegistrationApi(email, password, friendlyName) {
  const res = await apiClient.post('/auth/biometric/register/begin', { email, password, friendlyName });
  return unwrap(res); // BiometricRegisterStartResponse
}

/**
 * Complete biometric registration (step 2)
 */
export async function finishBiometricRegistrationApi(credentialData) {
  const res = await apiClient.post('/auth/biometric/register/finish', credentialData);
  const api = unwrap(res);
  return buildSession(api, null);
}

/**
 * Start biometric login (step 1)
 */
export async function startBiometricLoginApi(email) {
  const res = await apiClient.post('/auth/biometric/login/begin', { email });
  return unwrap(res); // BiometricLoginStartResponse
}

/**
 * Complete biometric login (step 2)
 */
export async function finishBiometricLoginApi(credentialData) {
  const res = await apiClient.post('/auth/biometric/login/finish', credentialData);
  const api = unwrap(res);
  return buildSession(api, null);
}

/**
 * Get user's biometric credentials
 */
export async function getBiometricCredentialsApi(token) {
  const res = await apiClient.get('/auth/biometric/credentials', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return unwrap(res); // List of BiometricCredentialResponse
}

/**
 * Revoke a biometric credential
 */
export async function revokeBiometricCredentialApi(credentialId, reason, token) {
  const res = await apiClient.delete(`/auth/biometric/credentials/${credentialId}`, {
    headers: { Authorization: `Bearer ${token}` },
    data: { reason },
  });
  return unwrap(res);
}

/**
 * Get biometric authentication status for current user
 */
export async function getBiometricStatusApi(token) {
  const res = await apiClient.get('/auth/biometric/status', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return unwrap(res); // { hasBiometricCredentials, credentialCount, credentials }
}

// Build the session object that AuthContext stores in localStorage
// countryCode is known on register (from the form); on login we fetch it from /tenants/me
async function buildSession(apiResp, countryCodeHint) {
  const token = apiResp.token;

  // For unverified registrations, token is null - skip API calls and build minimal session
  if (!token) {
    const countryCode = countryCodeHint || 'MY';
    const countryData = COUNTRIES.find((c) => c.code === countryCode) || COUNTRIES[0];

    return {
      token: null,
      user: {
        id:        apiResp.userId,
        name:      apiResp.fullName,
        email:     apiResp.email,
        phone:     '',
        role:      apiResp.role,
        language:  'en',
        createdAt: null,
      },
      farm: {
        id:         apiResp.tenantId,
        name:       '',
        country:    countryCode,
        currency:   countryData.currency,
        symbol:     countryData.symbol,
        subUserLimit: 1,
        planLimits: {},
      },
      plan:               apiResp.plan || 'free',
      subscriptionEndsAt: null,
      subscribedAt:       null,
      activeModules:      apiResp.activeModules || ['POULTRY'],
    };
  }

  // Fetch tenant (for farm name + country) and profile (for phone) in parallel
  const [tenant, profile] = await Promise.all([
    fetchTenantProfile(token),
    fetchMyProfile(token),
  ]);

  const countryCode = countryCodeHint || tenant.countryCode || 'MY';
  const countryData = COUNTRIES.find((c) => c.code === countryCode) || COUNTRIES[0];

  return {
    token,
    user: {
      id:        profile.id || apiResp.userId,
      name:      profile.fullName || profile.name || apiResp.fullName,
      email:     profile.email   || apiResp.email,
      phone:     profile.phone   || '',
      role:      profile.role    || apiResp.role,
      language:  profile.language || 'en',
      createdAt: profile.createdAt || null,
    },
    farm: {
      id:         tenant.id,
      name:       tenant.name,
      country:    countryCode,
      currency:   countryData.currency,
      symbol:     countryData.symbol,
      subUserLimit: tenant.subUserLimit ?? 1,
      planLimits: tenant.planLimits || {},
    },
    plan:               tenant.plan || apiResp.plan || 'free',
    subscriptionEndsAt: tenant.subscriptionEndsAt || null,
    subscribedAt:       tenant.subscribedAt || null,
    activeModules:      apiResp.activeModules || ['POULTRY'],
  };
}
